import React, { useEffect, useState, useMemo } from "react";
import { api, formatGBP } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ArrowRight, Truck, HandCoins, Camera, X, Scale, Sparkles } from "lucide-react";
import TermsNotice from "@/components/TermsNotice";

const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function Book() {
  const [step, setStep] = useState(1);
  const [cats, setCats] = useState([]);
  const [bulky, setBulky] = useState([]);
  const [slots, setSlots] = useState([]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    postcode: "",
    waste_category_id: "",
    estimated_weight_kg: "",
    load_type: "self",
    description: "",
    photos: [],
    bulky_items: {},
    slot_id: "",
    terms_accepted: false,
  });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, b, s] = await Promise.all([
        api.get("/pricing/waste-categories"),
        api.get("/pricing/bulky-items"),
        api.get("/availability"),
      ]);
      setCats(c.data);
      setBulky(b.data);
      setSlots(s.data);
      setForm((f) => ({ ...f, waste_category_id: f.waste_category_id || c.data[0]?.id || "" }));
    })();
  }, []);

  const bulkyEntries = useMemo(
    () =>
      Object.entries(form.bulky_items)
        .filter(([, q]) => q > 0)
        .map(([item_id, qty]) => ({ item_id, qty })),
    [form.bulky_items]
  );

  useEffect(() => {
    if (!form.waste_category_id || !Number(form.estimated_weight_kg)) {
      setQuote(null);
      return;
    }
    const run = async () => {
      try {
        const { data } = await api.post("/quote", {
          waste_category_id: form.waste_category_id,
          estimated_weight_kg: Number(form.estimated_weight_kg),
          load_type: form.load_type,
          bulky_items: bulkyEntries,
        });
        setQuote(data);
      } catch {/* ignore */}
    };
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [form.waste_category_id, form.estimated_weight_kg, form.load_type, bulkyEntries]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBulkyQty = (id, delta) =>
    setForm((f) => ({
      ...f,
      bulky_items: { ...f.bulky_items, [id]: Math.max(0, (f.bulky_items[id] || 0) + delta) },
    }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - form.photos.length);
    const dataUrls = [];
    for (const file of files) {
      if (file.size > 4 * 1024 * 1024) {
        toast.error(`${file.name} too large (max 4 MB)`);
        continue;
      }
      dataUrls.push(await fileToDataURL(file));
    }
    setF("photos", [...form.photos, ...dataUrls]);
  };

  const removePhoto = (i) => setF("photos", form.photos.filter((_, idx) => idx !== i));

  const canStep2 = form.name && form.email && form.phone && form.address && form.postcode;
  const canStep3 = form.waste_category_id && Number(form.estimated_weight_kg) > 0;
  const canSubmit = canStep2 && canStep3 && form.slot_id && form.terms_accepted;

  const submitBooking = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        postcode: form.postcode,
        waste_category_id: form.waste_category_id,
        estimated_weight_kg: Number(form.estimated_weight_kg),
        load_type: form.load_type,
        description: form.description,
        photos: form.photos,
        bulky_items: bulkyEntries,
        slot_id: form.slot_id,
      });
      const { data: session } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = session.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Book a collection</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-white">
        Tell us what to take. <span className="text-[#8DC63F]">Pay only for what we take.</span>
      </h1>

      <div className="grid lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-8 space-y-6">
          <Stepper step={step} />

          {step === 1 && (
            <div className="wa-card wa-fadeup" data-testid="step-1-card">
              <h2 className="font-display text-xl font-bold mb-4 text-white">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Full name">
                  <input className="wa-input" value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="input-name" />
                </Field>
                <Field label="Email">
                  <input type="email" className="wa-input" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="input-email" />
                </Field>
                <Field label="Phone">
                  <input className="wa-input" value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="input-phone" />
                </Field>
                <Field label="Postcode">
                  <input className="wa-input uppercase" value={form.postcode} onChange={(e) => setF("postcode", e.target.value.toUpperCase())} data-testid="input-postcode" />
                </Field>
                <Field label="Collection address" className="sm:col-span-2">
                  <input className="wa-input" value={form.address} onChange={(e) => setF("address", e.target.value)} data-testid="input-address" />
                </Field>
              </div>
              <div className="flex justify-end mt-4">
                <button disabled={!canStep2} onClick={() => setStep(2)} className="wa-btn-primary disabled:opacity-40" data-testid="step-1-next">
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="wa-card wa-fadeup" data-testid="step-2-card">
              <h2 className="font-display text-xl font-bold mb-4 text-white">Your waste</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Waste type">
                  <select className="wa-input" value={form.waste_category_id} onChange={(e) => setF("waste_category_id", e.target.value)} data-testid="input-waste-category">
                    {cats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {formatGBP(c.sale_per_kg)}/kg
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Estimated weight (kg)">
                  <input type="number" min="0" className="wa-input" value={form.estimated_weight_kg} onChange={(e) => setF("estimated_weight_kg", e.target.value)} data-testid="input-weight" />
                </Field>
              </div>

              <div className="mt-4">
                <div className="wa-label">Load type</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <button
                    onClick={() => setF("load_type", "self")}
                    className={`relative flex flex-col items-start gap-1 px-3 py-3 rounded-xl border text-sm font-semibold transition ${form.load_type === "self" ? "border-[#8DC63F] bg-[#8DC63F] text-black" : "border-[#232428] bg-black text-white hover:bg-white/5"}`}
                    data-testid="select-load-self"
                  >
                    <span className="flex items-center gap-2"><HandCoins size={16} /> Self load</span>
                    {quote && quote.self_load_saving > 0 && (
                      <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full ${form.load_type === "self" ? "bg-black/15 text-black" : "bg-[#8DC63F] text-black"}`} data-testid="book-self-load-saving-badge">
                        Save {formatGBP(quote.self_load_saving)}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setF("load_type", "we")}
                    className={`flex flex-col items-start gap-1 px-3 py-3 rounded-xl border text-sm font-semibold transition ${form.load_type === "we" ? "border-[#8DC63F] bg-[#8DC63F] text-black" : "border-[#232428] bg-black text-white hover:bg-white/5"}`}
                    data-testid="select-load-we"
                  >
                    <span className="flex items-center gap-2"><Truck size={16} /> We load (roadside)</span>
                    {quote && quote.we_load_surcharge_percent > 0 && (
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${form.load_type === "we" ? "text-black/70" : "text-gray-400"}`}>
                        +{quote.we_load_surcharge_percent}% loading surcharge
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {bulky.length > 0 && (
                <div className="mt-4">
                  <div className="wa-label">Bulky items</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {bulky.map((b) => {
                    const qty = form.bulky_items[b.id] || 0;
                    return (
                      <div key={b.id} className={`rounded-xl border p-2 text-xs ${qty > 0 ? "border-[#8DC63F] bg-[#8DC63F]/5" : "border-[#232428] bg-[#0a0a0a]"}`} data-testid={`bulky-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <div className="font-semibold text-white">{b.name}</div>
                        <div className="text-gray-400">{formatGBP(b.sale_price)} ea</div>
                        <div className="flex items-center justify-between mt-2">
                          <button onClick={() => setBulkyQty(b.id, -1)} className="w-6 h-6 rounded-full bg-white/10 text-white">−</button>
                          <span className="font-bold text-white">{qty}</span>
                          <button onClick={() => setBulkyQty(b.id, 1)} className="w-6 h-6 rounded-full bg-[#8DC63F] text-black">+</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              <Field label="Description of items" className="mt-4">
                <textarea rows={3} className="wa-input" value={form.description} onChange={(e) => setF("description", e.target.value)} data-testid="input-description" />
              </Field>

              <Field label="Photos (helps us pre-quote, max 6, 4 MB each)" className="mt-4">
                <label className="flex items-center gap-2 wa-btn-outline cursor-pointer w-fit" data-testid="upload-photos-label">
                  <Upload size={16} /> Upload photos
                  <input type="file" multiple accept="image/*" hidden onChange={handlePhotos} data-testid="upload-photos-input" />
                </label>
                {form.photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                    {form.photos.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-[#232428]">
                        <img src={p} alt={`upload ${i}`} className="w-full h-full object-cover" />
                        <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-white grid place-items-center" data-testid={`remove-photo-${i}`}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>

              <TermsNotice className="mt-5" />

              <div className="flex justify-between mt-4">
                <button onClick={() => setStep(1)} className="wa-btn-ghost">Back</button>
                <button disabled={!canStep3} onClick={() => setStep(3)} className="wa-btn-primary disabled:opacity-40" data-testid="step-2-next">
                  Pick a slot <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="wa-card wa-fadeup" data-testid="step-3-card">
              <h2 className="font-display text-xl font-bold mb-4 text-white">Pick a collection slot</h2>
              {slots.length === 0 ? (
                <div className="text-sm text-gray-400">No slots currently available — please <a href="/contact" className="underline text-[#8DC63F]">contact us</a> to arrange one.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                  {slots.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setF("slot_id", s.id)}
                      className={`text-left rounded-xl border p-3 transition ${form.slot_id === s.id ? "border-[#8DC63F] bg-[#8DC63F] text-black" : "border-[#232428] bg-[#0a0a0a] text-white hover:bg-white/5"}`}
                      data-testid={`slot-${s.id}`}
                    >
                      <div className="text-xs uppercase tracking-wider opacity-70">{s.date}</div>
                      <div className="font-bold">{s.start_time} – {s.end_time}</div>
                      {s.label && <div className="text-xs opacity-70 mt-1">{s.label}</div>}
                    </button>
                  ))}
                </div>
              )}
              <TermsNotice className="mt-5" />

              <label
                className={`mt-4 flex items-start gap-3 p-3 rounded-xl border cursor-pointer select-none transition ${form.terms_accepted ? "border-[#8DC63F] bg-[#8DC63F]/8" : "border-[#232428] bg-[#0a0a0a]"}`}
                style={form.terms_accepted ? { background: "rgba(141, 198, 63, 0.08)" } : {}}
                data-testid="terms-accept-row"
              >
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={(e) => setF("terms_accepted", e.target.checked)}
                  className="mt-1 accent-[#8DC63F] w-4 h-4"
                  data-testid="terms-accept-checkbox"
                />
                <span className="text-sm text-gray-200">
                  I&apos;ve read and accept the terms above — items not on my quote may be charged at a higher walk-up rate or refused, Weigh Away may refuse a collection, and waste may be offloaded if payment isn&apos;t completed.
                </span>
              </label>

              <div className="flex justify-between mt-4">
                <button onClick={() => setStep(2)} className="wa-btn-ghost">Back</button>
                <button disabled={!canSubmit || submitting} onClick={submitBooking} className="wa-btn-primary disabled:opacity-40" data-testid="submit-booking-pay-deposit">
                  Pay deposit & confirm
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-24 space-y-4">
            <div className="wa-card" data-testid="summary-card">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">Live estimate</div>
              {quote ? (
                <>
                  <div className="font-display text-3xl font-black mt-1 text-[#8DC63F]">{formatGBP(quote.estimated_total)}</div>
                  <div className="text-xs text-gray-400">Deposit: {formatGBP(quote.deposit_due)} · Balance: {formatGBP(quote.balance_due)}</div>
                  <div className="mt-3 space-y-1 text-sm">
                    <Row k="Weight charge" v={formatGBP(quote.base_weight_charge)} />
                    {quote.bulky_total > 0 && <Row k="Bulky surcharges" v={formatGBP(quote.bulky_total)} />}
                    {quote.we_load_surcharge > 0 && (
                      <Row k={`We-load surcharge (+${quote.we_load_surcharge_percent}%)`} v={formatGBP(quote.we_load_surcharge)} />
                    )}
                    {quote.loading_fee > 0 && <Row k="We-load fee" v={formatGBP(quote.loading_fee)} />}
                    {quote.self_load_discount > 0 && <Row k="Self-load discount" v={`− ${formatGBP(quote.self_load_discount)}`} />}
                  </div>

                  {quote.suggested_skip && <SkipCompareInline quote={quote} />}
                </>
              ) : (
                <div className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                  <Camera size={16} /> Add waste details to see your estimate.
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500">
              Estimate is based on weight you provide. Final price is calculated on actual weight at collection.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const items = ["Details", "Waste", "Slot & pay"];
  return (
    <div className="flex items-center gap-2" data-testid="stepper">
      {items.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <React.Fragment key={label}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${active ? "bg-white text-black" : done ? "bg-[#8DC63F] text-black" : "bg-[#16171A] text-gray-300 border border-[#232428]"}`}>
              <span className="font-bold">{n}</span> {label}
            </div>
            {i < items.length - 1 && <div className="flex-1 h-px bg-[#232428]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="wa-label">{label}</label>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400">{k}</span>
      <span className="font-semibold text-white">{v}</span>
    </div>
  );
}

function SkipCompareInline({ quote }) {
  const s = quote.suggested_skip;
  const mid = (s.price_low + s.price_high) / 2;
  const saving = Math.max(0, mid - quote.estimated_total);
  return (
    <div
      className="mt-4 rounded-xl border-2 border-[#8DC63F] p-3"
      style={{ background: "rgba(141, 198, 63, 0.08)" }}
      data-testid="summary-skip-comparison"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#8DC63F]">
        <Scale size={12} /> Skip comparison
      </div>
      <p className="text-xs text-gray-200 mt-1.5">
        A <span className="font-bold text-white">{s.name}</span> costs around{" "}
        <span className="font-bold text-white">{formatGBP(s.price_low)}–{formatGBP(s.price_high)}</span>.
        With Weigh Away you pay <span className="font-bold text-[#8DC63F]">{formatGBP(quote.estimated_total)}</span> — and only for what we take.
      </p>
      {saving > 0 && (
        <div
          className="mt-3 rounded-xl bg-black border-2 border-[#8DC63F] p-4 text-center"
          data-testid="summary-skip-savings-card"
          style={{ boxShadow: "0 8px 24px rgba(141, 198, 63, 0.22)" }}
        >
          <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#8DC63F] font-bold">
            <Sparkles size={12} /> You could save
          </div>
          <div
            className="font-display font-black text-[#8DC63F] leading-none mt-1.5"
            style={{ fontSize: "clamp(2rem, 5vw, 3rem)" }}
            data-testid="summary-skip-savings"
          >
            {formatGBP(saving)}
          </div>
          <div className="text-[10px] text-gray-400 mt-1">
            vs the typical mid-price of a {s.name.toLowerCase()}
          </div>
        </div>
      )}
    </div>
  );
}
